import { useEffect, useRef, useState } from "react";
import { ShieldAlert, X } from "lucide-react";
import api, { leaveSupportSession } from "@/modules/shared/api/axios";

const clock = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

export default function SupportSessionBanner() {
  const [session, setSession] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [exiting, setExiting] = useState(false);
  const checkedAtExpiry = useRef(false);

  useEffect(() => {
    let alive = true;
    api.get("/support/current", { forceRefresh: true, metadata: { trackRequest: false } })
      .then((response) => alive && setSession(response?.data?.data || null))
      .catch(() => alive && setSession(null));
    return () => { alive = false; };
  }, []);

  // Count down to the end of the session. This clock may differ from the
  // server's, so reaching zero only asks the server; if the session really is
  // over it answers 401 and the app leaves the customer's screens.
  useEffect(() => {
    if (!session?.active || !session?.expires_at) return undefined;
    const endsAt = new Date(session.expires_at).getTime();
    if (Number.isNaN(endsAt)) return undefined;

    const tick = () => {
      const left = Math.max(0, Math.round((endsAt - Date.now()) / 1000));
      setRemaining(left);
      if (left === 0 && !checkedAtExpiry.current) {
        checkedAtExpiry.current = true;
        api.get("/support/current", { forceRefresh: true, metadata: { trackRequest: false } }).catch(() => {});
      }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [session?.active, session?.expires_at]);

  if (!session?.active) return null;

  const exit = async () => {
    if (exiting) return;
    setExiting(true);
    try {
      // Ends the session on the server so its token stops working everywhere.
      await api.post("/support/exit", null, { timeout: 8000, metadata: { trackRequest: false } });
    } catch (_) {
      // Leave regardless: this tab forgets the token, and the server ends the
      // session on its own when the 15 minutes are up.
    }
    leaveSupportSession("ended");
  };

  return (
    <div className="support-session-banner" role="status">
      <ShieldAlert size={16} aria-hidden="true" />
      <span>Support mode: read-only access. Changes, payments, passwords, and account settings are blocked.</span>
      {remaining !== null && (
        <span className={`support-session-time${remaining <= 60 ? " is-ending" : ""}`} aria-label="Time left in this support session">
          {clock(remaining)}
        </span>
      )}
      <button type="button" onClick={exit} disabled={exiting} className="support-session-exit">
        <X size={15} aria-hidden="true" /> {exiting ? "Ending…" : "Exit support"}
      </button>
    </div>
  );
}
